import * as React from 'react'
import {
  Label,
  LabelProps,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  XAxis,
  YAxis,
} from 'recharts'
import '../exposure-chart.css'
import { GUID } from '../guids'
import '../recharts.css'
import * as result from '../result'
import { Result } from '../result'
import {
  AlertLevel,
  ClientExposuresTransactions,
  LimitType,
  AlertLevels,
  AlertLevelsDict,
  MutedMetrics,
} from '../types/types'
import {
  assertNever,
  customAlertLevelBadge,
  getHighestLevel,
  obfuscateSymbol,
  unitToPercentString,
} from '../utils'
import { unitize } from './ManageAlerts'
import { ClientAlert, ClientCode, ClientConfig } from "../types/clients";


export interface Props {
  readonly clearSelection: () => void
  readonly clients: ReadonlyArray<ClientExposuresTransactions>
  readonly guidLookup: Record<ClientCode, GUID | undefined>
  readonly metricLevels: AlertLevelsDict
  readonly selectedClientCode: ClientCode | undefined
  readonly toggleMute: (clientCode: ClientCode, metric: LimitType) => () => void
  readonly tvMode: boolean
  readonly onClientSelectorClick: (selectBySymbol: string) => void
  readonly activeClientInfo: Result<ActiveClientInfo, NoActiveClientReasons>
}

export interface ActiveClientInfo {
  readonly state: ClientConfig
  readonly metricLevels: AlertLevels
  readonly mutedMetrics: MutedMetrics
  readonly values: ClientExposuresTransactions
}

export type NoActiveClientReasons =
  | 'no client selected'
  | 'no settings found for given client'
  | 'no data for given client'

type AxisName = 'Exposure' | 'Transactions' | 'UnactionedOrders'

const domain = [0, 100]
const chartMargin = { top: 30, right: 30, bottom: 30, left: 30 }

const xAxisLabel: string | number | LabelProps = {
  angle: 0,
  offset: -20,
  position: 'insideBottomRight',
  value: 'Unactioned Orders',
}

const yAxisLabel: string | number | LabelProps = {
  angle: -90,
  offset: -15,
  position: 'insideLeft',
  value: 'Exposure',
  style: { textAnchor: 'middle' }
}

const xAxisName: AxisName = 'UnactionedOrders'
const yAxisName: AxisName = 'Exposure'

const xAxisDataKey: keyof ClientExposuresTransactions =
  'rc6unactionedOrdersPercent'

const yAxisDataKey: keyof ClientExposuresTransactions = 'rc2exposurePercent'

function ExposureChart(props: Props): JSX.Element {
  const {
    clients,
    metricLevels,
    selectedClientCode,
    tvMode,
    onClientSelectorClick,
  } = props

  // tslint:disable-next-line:readonly-array
  const clientsTypeWorkaround = clients as ClientExposuresTransactions[]

  return (
    <div
      className={
        selectedClientCode !== undefined && !tvMode
          ? 'main-chart-grid-with-details'
          : 'main-chart-grid'
      } style={{ width: '100%' }}
    >
      <ResponsiveContainer width='80%' height={820}>
        <ScatterChart margin={chartMargin}>
          <XAxis
            name={xAxisName}
            label={xAxisLabel}
            dataKey={xAxisDataKey}
            unit='%'
            domain={domain}
            tickFormatter={tickFormatter}
            type='number' />
          <YAxis
            name={yAxisName}
            label={yAxisLabel}
            dataKey={yAxisDataKey}
            unit='%'
            domain={domain}
            tickFormatter={tickFormatter}
            type='number'
          />
          <ReferenceLine
            x={75}
            stroke='#88888888'
            strokeDasharray='3 3'
            label='75%'
          />
          <ReferenceLine
            y={75}
            stroke='#88888888'
            strokeDasharray='3 3'
            label='75%'
          />
          <ReferenceLine
            x={90}
            stroke='#88888888'
            strokeDasharray='3 3'
            label='90%'
          />
          <ReferenceLine
            y={90}
            stroke='#88888888'
            strokeDasharray='3 3'
            label='90%'
          />
          <ReferenceLine
            x={100}
            stroke='#88888888'
            strokeDasharray='3 3'
            label='100%'
          />
          <ReferenceLine
            y={100}
            stroke='#88888888'
            strokeDasharray='3 3'
            label='100%'
          />
          <Scatter
            name='Client'
            fill='#82ca9d'
            data={clientsTypeWorkaround}
            // TODO: using this custom shape greatly increases the number of repaints; can we avoid that?
            shape={scatterPoint(
              clients.map(c => c.code),
              tvMode,
              metricLevels,
              selectedClientCode,
            )}
            onMouseDown={onClientSelectorClick}
          />
        </ScatterChart>
      </ResponsiveContainer>
      {tvMode ? null : clientInfo(props)}

      <div className='chart-point-key'>
        <span>Key: </span>
        {alertLevelBadge('no onClick', 'Breach')}
        {alertLevelBadge('no onClick', 'Warn')}
        {alertLevelBadge('no onClick', 'Notify')}
      </div>
    </div>
  )
}

export function clientInfo({
  activeClientInfo,
  clearSelection,
  selectedClientCode,
  toggleMute,
}: Props): JSX.Element {
  function onOk({
    state,
    metricLevels,
    mutedMetrics,
    values,
  }: ActiveClientInfo): JSX.Element {
    const hasAName: boolean = state.name.length > 0 && state.code !== state.name
    const lowestAlertsFirst = [...state.alerts].sort(
      (alertA, alertB) => alertA.thresholdInPercent - alertB.thresholdInPercent,
    )
    const rc2Alerts = lowestAlertsFirst.filter(
      alert => alert.limitType === 'RC2_exposure',
    )
    const rc6Alerts = lowestAlertsFirst.filter(
      alert => alert.limitType === 'RC6_unactioned_orders',
    )
    const revAlerts = lowestAlertsFirst.filter(
      alert => alert.limitType === 'Realistic_exposure_value',
    )
    return (
      <div className='text-center'>
        <h1 className='no-margin-top'>
          {state.code}
          {hasAName ? ' (' + state.name + ')' : ''}
        </h1>
        <button className='button' onClick={clearSelection}>
          Clear selection
        </button>
        <div className='chart-detailed-info'>
          <div className='chart-detailed-info__item'>
            <h2 className='text-center'>
              Exposure (RC2)
              <br />
              {alertLevelBadge('no onClick', metricLevels.rc2)},{' '}
              {unitToPercentString(values.rc2exposurePercent)}
            </h2>
            {muteToggler(toggleMute, mutedMetrics, state.code, 'RC2_exposure')}
            <div className='left-right-split'>
              <span>Current: </span>
              <span className='left-right-split__separator' />
              <span>{unitize('RC2_exposure', values.rc2exposureInYen)}</span>
            </div>
            {rc2Alerts.map(
              alertToDetails('RC2_exposure', state.exposureLimitInJpy),
            )}
          </div>
          <div className='chart-detailed-info__item margin-top-2-em'>
            <h2 className='text-center'>
              Unactioned Orders (RC6)
              <br />
              {alertLevelBadge('no onClick', metricLevels.rc6)},{' '}
              {unitToPercentString(values.rc6unactionedOrdersPercent)}
            </h2>
            {muteToggler(toggleMute, mutedMetrics, state.code, 'RC6_unactioned_orders')}
            <div className='left-right-split'>
              <span>Current: </span>
              <span className='left-right-split__separator' />
              <span>
                {unitize('RC6_unactioned_orders', values.rc6unactionedOrders)}
              </span>
            </div>
            {rc6Alerts.map(alertToDetails('RC6_unactioned_orders', state.unactionedOrdersLimit),
            )}
          </div>
          <div className='chart-detailed-info__item margin-top-2-em'>
            <h2 className='text-center'>
              Realistic Exposure Value (REV)
              <br />
              {alertLevelBadge('no onClick', metricLevels.rev)},{' '}
              {unitToPercentString(values.revExposureOrdersPercent)}
            </h2>
            {muteToggler(toggleMute, mutedMetrics, state.code, 'Realistic_exposure_value')}
            <div className='left-right-split'>
              <span>Current: </span>
              <span className='left-right-split__separator' />
              <span>
                {unitize('Realistic_exposure_value', values.revExposureOrdersValue)}
              </span>
            </div>
            <div className='left-right-split'>
              <span>Current Day Max: </span>
              <span className='left-right-split__separator' />
              <span>
                {unitize('Historic_Max_Realistic_exposure', values.maxRevExposureOrdersValue)}
              </span>
            </div>
            <div className='left-right-split'>
              <span>Historic Max: </span>
              <span className='left-right-split__separator' />
              <span>
                {unitize('Historic_Max_Realistic_exposure', values.historicMaxRevExposureValue)}
              </span>
            </div>
            <div className='left-right-split'>
              <span>Historic Max %: </span>
              <span className='left-right-split__separator' />
              <span>
                {unitToPercentString( values.historicMaxRevExposurePercent)}
              </span>
            </div>
            {revAlerts.map(alertToDetailsForRealisticExposureValue('Realistic_exposure_value', state.realisticExposureLimit))}
          </div>
        </div>
      </div>
    )
  }

  function onErr(err: NoActiveClientReasons): JSX.Element {
    switch (err) {
      case 'no client selected':
        return <div />
      case 'no settings found for given client':
        return (
          <div>
            Whoops! We couldn't find the settings for client "
            {selectedClientCode}"
          </div>
        )
      case 'no data for given client':
        return (
          <div>
            Whoops! We don't have any data for client "{selectedClientCode}"
          </div>
        )
      default:
        return assertNever(err)
    }
  }

  return result.merge(onOk, onErr, activeClientInfo)
}

function muteToggler(
  toggleMute: Props['toggleMute'],
  mutedMetrics: MutedMetrics,
  clientCode: ClientCode,
  metric: LimitType,
): JSX.Element {
  return (
    <button className='button' onClick={toggleMute(clientCode, metric)}>
      {mutedMetrics[metric] === 'muted' ? 'Un-mute' : 'Mute'}
    </button>
  )
}

function alertToDetails(
  metric: LimitType,
  max: number,
): (a: ClientAlert) => JSX.Element {
  return function _alertToDetails(alert: ClientAlert): JSX.Element {
    return (
      <div key={alert.thresholdInPercent} className='left-right-split'>
        <span>{unitToPercentString(alert.thresholdInPercent)} limit: </span>
        <span className='left-right-split__separator' />
        <span>
          {unitize(metric, Math.round((alert.thresholdInPercent / 100) * max))}
        </span>
      </div>
    )
  }
}
function alertToDetailsForRealisticExposureValue(
  metric: LimitType,
  max: number,
): (a: ClientAlert) => JSX.Element {
  return function _alertToDetails(alert: ClientAlert): JSX.Element {
      return (
        <div key={alert.thresholdInPercent} className='left-right-split'>
          <span>{unitToPercentString(alert.thresholdInPercent)} limit: </span>
          <span className='left-right-split__separator' />
          <span>
            {unitize(metric, Math.round((alert.thresholdInPercent / 100) * max))}
          </span>
        </div>
      )
  }
}

function tickFormatter(value: number): string {
  return value.toFixed(0)
}

interface ScatterPointProps {
  readonly cx: number
  readonly cy: number
  readonly fill: string
  readonly height: number
  readonly key: string
  readonly node: {
    readonly x: number
    readonly y: number
    readonly z: '-'
  }
  readonly payload: ClientExposuresTransactions
  readonly size: number
  readonly tooltipPosition: {
    readonly x: number
    readonly y: number
  }
  readonly width: number
  readonly x: number
  readonly y: number
  // do we need to implement types for these?
  // tooltipPayload:
  // xAxis:
  // yAxis:
  // zAxis:
}

type CustomShapeFunction = (props: unknown) => JSX.Element;

function alertLevelBadge(
  onBadgeClick: 'no onClick' | ((level: AlertLevel) => () => void),
  level: AlertLevel,
): JSX.Element {
  return customAlertLevelBadge('', level, displayLevel(level), onBadgeClick)
}

function displayLevel(level: AlertLevel): string {
  switch (level) {
    case 'Baseline':
      return 'Ok'
    case 'Notify':
    case 'Warn':
    case 'Breach':
      return level
    default:
      return assertNever(level)
  }
}

function scatterPoint(
  clientCodes: ReadonlyArray<ClientCode>,
  tvMode: boolean,
  metricLevels: AlertLevelsDict,
  selectedClient: ClientCode | undefined,
): CustomShapeFunction {
  return function _scatterPoint(props: unknown): JSX.Element {
    if (!isScatterPointProps(props)) {
      throw new Error("Invalid props passed to scatterPoint");
    }

    const { fill, width, height, x, y, cx, cy, payload } = props; // Destructuring is safe after type check

    const isSelected = selectedClient === payload.code

    const groupClasses = [
      'recharts-layerrecharts-scatter-symbol',
      'dashboard-point alert-level-badge--' +
      getHighestLevel(
        metricLevels,
        {},
        payload.code,
      ).toLowerCase(),
      isSelected ? ' dashboard-point--selected' : '',
    ].join(' ')

    return (
      <g
        className={groupClasses}
        transform={'translate(' + cx + ' ' + cy + ')'}
      >
        <circle r={5} cx={0} cy={0} className='dashboard-point__fill' />
        {/* Width & height are semi-arbitrary, but should be large enough to
        contain the symbol */}
        <foreignObject
          className='node'
          width={100}
          height={40}
          x={-10}
          y={-40}
          style={{ pointerEvents: 'none' }}
        >
          <div className='scatter-point__text__wrapper'>
            <div className='scatter-point__text alert-level-badge__text'>
              {tvMode ? <span className='tv-mode-symbol'>{obfuscateSymbol(clientCodes.indexOf(payload.code))}</span> : payload.code}
            </div>
          </div>
        </foreignObject>
      </g>
    )
  }
}

// ensuring data structures passed are type-safe and clearly defined
function isScatterPointProps(props: any): props is ScatterPointProps {
  return typeof props === 'object' &&
    props !== null &&
    'cx' in props && typeof props.cx === 'number' &&
    'cy' in props && typeof props.cy === 'number' &&
    'payload' in props && typeof props.payload === 'object' &&
    'code' in props.payload; // Ensure payload structure matches
}

export default ExposureChart
